import { SelectedMenuProductList, SelectedMenu } from 'rangeConfig/@types';
import ChangeSize from 'rangeConfig/pax/widgets/changeSize/ChangeSize';
import ChangeCornerSolutionParts from 'rangeConfig/pax/widgets/ChangeCornerSolutionParts';
import ChangeOrientation from 'rangeConfig/pax/widgets/ChangeOrientation';
import { CoreApi, ProductType } from '@inter-ikea-storageone/range';
import deleteProduct from 'rangeConfig/shared/behaviors/deleteProduct';
import hideDoors from 'rangeConfig/shared/behaviors/hideDoors';
import addToTarget from 'rangeConfig/shared/behaviors/addToTarget';
import addProduct from 'rangeConfig/shared/behaviors/addProduct';
import addInteriorSuggestion from 'rangeConfig/pax/behaviors/addInteriorSuggestion';
import { interiorsTypeFilter } from '../../filters/configFilters';
import platform from 'rangeConfig/shared/utils/platform';
import { noneOptionForInteriorSuggestions } from 'rangeConfig/shared/widgets/interiorSuggestions/noneOptionForInteriorSuggestions';
import {
  matchDesign,
  matchDimensions,
  matchType
} from 'rangeConfig/shared/filters/selectedMenuProductFilters';
import {
  matchApplicableFrame,
  matchDoorToFrame,
  matchInterior,
  matchInteriorSuggestionDimensions
} from 'rangeConfig/pax/filters/selectedMenuProductFilters';
import autoFillDoors from 'rangeConfig/pax/behaviors/autoFillDoors';
import showDoors from 'rangeConfig/shared/behaviors/showDoors';
import replaceProduct from 'rangeConfig/shared/behaviors/replaceProduct';
import DimensionsLabel from 'rangeConfig/shared/widgets/DimensionsLabel';
import { isSelectedEntityProductType } from 'rangeConfig/shared/rules/hideMenuRules';
import showClothHangersSheet from 'rangeConfig/shared/behaviors/showClothHangersSheet';

const interiorSuggestions: SelectedMenuProductList = {
  id: 'frames-selected-menu-interior-suggestions',
  title: 'storageone.selectedMenuInteriorsCombinationsMenuTitle',
  cardsTitle: '',
  cardsSubtitle: '',
  additionalItemsText: 'storageone.selectedProductAdditionalInteriorSuggestions',
  productThumbToolTip: 'name',
  behaviors: {
    productClicked: [hideDoors, addInteriorSuggestion],
    productDropped: [hideDoors, addInteriorSuggestion]
  },
  productTypes: [ProductType.PAX_INTERIOR_SUGGESTIONS],
  productFilters: [matchInteriorSuggestionDimensions, matchDesign, matchApplicableFrame],
  noneOption: noneOptionForInteriorSuggestions
};

const doors: SelectedMenuProductList = {
  id: 'frames-selected-menu-doors',
  title: 'storageone.doorCategoryTitleHingedDoors',
  cardsTitle: '',
  cardsSubtitle: '',
  additionalItemsText: 'storageone.selectedProductAdditionalDoors',
  productThumbToolTip: 'name',
  behaviors: {
    productClicked: [
      showDoors,
      autoFillDoors(['S40301124_ASM', 'S10301125_ASM', 'S20414829_ASM', 'S00414830_ASM'])
    ],
    productDropped: [
      showDoors,
      autoFillDoors(['S40301124_ASM', 'S10301125_ASM', 'S20414829_ASM', 'S00414830_ASM'])
    ]
  },
  noThumbs: () => true,
  productTypes: [ProductType.PAX_HINGE_DOOR_ASM],
  productFilters: [matchDoorToFrame]
};

const widgets = {
  desktop: [
    {
      id: 'display-size',
      component: DimensionsLabel,
      title: 'storageone.sizeFilterLabel',
      hidden: (coreApi: CoreApi) =>
        isSelectedEntityProductType(coreApi, ProductType.PAX_CORNER_SOLUTION_ASM)
    },
    {
      id: 'change-size',
      component: ChangeSize,
      hidden: (coreApi: CoreApi) =>
        isSelectedEntityProductType(coreApi, ProductType.PAX_CORNER_SOLUTION_ASM)
    },
    {
      id: 'change-parts',
      component: ChangeCornerSolutionParts,
      hidden: (coreApi: CoreApi) =>
        !isSelectedEntityProductType(coreApi, ProductType.PAX_CORNER_SOLUTION_ASM)
    },
    {
      id: 'change-orientation',
      component: ChangeOrientation,
      hidden: (coreApi: CoreApi) =>
        !isSelectedEntityProductType(coreApi, ProductType.PAX_CORNER_SOLUTION_ASM)
    },
    {
      id: 'frames-selected-menu-colors',
      title: 'storageone.frameColorFilterTitle',
      cardsTitle: '',
      cardsSubtitle: 'typeName',
      additionalItemsText: 'storageone.selectedProductAdditionalFrames',
      behaviors: {
        productClicked: [replaceProduct],
        productDropped: []
      },
      productTypes: [ProductType.PAX_FRAME, ProductType.PAX_CORNER_SOLUTION_ASM],
      filters: [],
      productFilters: [matchType, matchDimensions],
      useMLSorting: true
    },
    {
      id: 'frames-selected-menu-interiors',
      title: 'storageone.productListInteriorsTitle',
      cardsTitle: '',
      cardsSubtitle: 'typeName',
      additionalItemsText: 'storageone.selectedProductAdditionalInteriors',
      behaviors: {
        productClicked: [hideDoors, addToTarget],
        productDropped: [hideDoors, addProduct]
      },
      productTypes: [
        ProductType.PAX_DRAWER,
        ProductType.PAX_DRAWER_FRAMED,
        ProductType.PAX_DRAWER_FRAMED_GLASS,
        ProductType.PAX_DRAWER_LOCKABLE,
        ProductType.PAX_GLASS_DRAWER,
        ProductType.PAX_SHELF,
        ProductType.PAX_GLASS_SHELF,
        ProductType.PAX_PULL_OUT_TRAY,
        ProductType.PAX_PULL_OUT_TRAY_WITH_DIVIDER_INSERTS_ASM,
        ProductType.PAX_PULL_OUT_TRAY_WITH_MAT_ASM,
        ProductType.PAX_PULL_OUT_TRAY_WITH_COMPARTMENT_INSERTS_ASM,
        ProductType.PAX_PULL_OUT_TRAY_WITH_SHOE_INSERTS_ASM,
        ProductType.PAX_CLOTH_RAIL_350,
        ProductType.PAX_CLOTH_RAIL_580,
        ProductType.PAX_VALET_HANGER,
        ProductType.PAX_T_DIVIDER,
        ProductType.PAX_L_DIVIDER,
        ProductType.PAX_PULL_OUT_MULTI_USE_HANGER,
        ProductType.PAX_PULL_OUT_TROUSER_HANGER,
        ProductType.PAX_PULL_OUT_CLOTH_RAIL,
        ProductType.PAX_SHOE_SHELF,
        ProductType.PAX_PULL_OUT_SHOE_SHELF,
        ProductType.PAX_MESH_BASKET_ASM,
        ProductType.PAX_METAL_BASKET_ASM,
        ProductType.PAX_CLEANING_INTERIOR
      ],
      filters: [interiorsTypeFilter],
      productFilters: [matchInterior],
      useMLSorting: true
    },
    interiorSuggestions
  ],
  mobile: [
    {
      id: 'change-orientation-cornersolution',
      title: 'storageone.changeOrientation',
      component: ChangeOrientation,
      hidden: (coreApi: CoreApi) =>
        !isSelectedEntityProductType(coreApi, ProductType.PAX_CORNER_SOLUTION_ASM)
    },
    {
      id: 'change-width-cornersolution',
      title: 'storageone.width',
      component: ChangeCornerSolutionParts,
      hidden: (coreApi: CoreApi) =>
        !isSelectedEntityProductType(coreApi, ProductType.PAX_CORNER_SOLUTION_ASM)
    },
    interiorSuggestions,
    doors
  ]
};

const selectedMenu: SelectedMenu = {
  menuTitle: 'storageone.selectedProductListCustomiseFrame',
  behaviors: {
    productDeleted: [deleteProduct],
    selectedMenuExited: [showClothHangersSheet]
  },
  content: widgets[platform]
};

export default selectedMenu;
